import { RMA_STATUS_TRANSLATION_KEY } from "../../../services/api/rma-list-service";
import { RESOLUTION_ID } from "../../resolutions/constants";
import { FEE_TYPE, IPolicyFee, IPolicyRule } from "../../policies/policy-types";
import { IAggregatorInfo, IReturnMethod, RETURN_METHOD_TYPE } from "../../return-methods/return-method-types";
import {IManagerCredentials} from "../../../manager-approval-modal/manager-credentials-stores";
import { INCENTIVE_VIRTUAL_CURRENCIES } from '../../../models/User';
import { IResolution } from "../../../models/ResolutionTypes";
import { SHIPPING_CONNECTION_CODE } from "../../../models/IntegrationsTypes";


export interface IRmaPolicyRule extends Pick<IPolicyRule,
    "id" |
    "name" |
    "charge_fees"> {
    policy_rule_fees: IRmaPolicyRuleFee[];
    merchant_resolution_setting: IRmaResolution;
}

export interface IRmaPolicyRuleFee extends Partial<IPolicyFee> {
    id: number;
    is_charged: boolean;
    /* Please note this type is not transferred AS IS from IPolicyFee - there it's FEE_TYPE | null :O */
    fee_type: FEE_TYPE;
}

export interface IRmaResolution extends Partial<IResolution> {
    id: number;
    internal_name: string;
    resolution_id: RESOLUTION_ID;
}

export interface IRmaReturnReason {
    id: number;
    _internal_name: string;
}
export interface IRmaSettings {
    return_methods: IRMAReturnMethodPreset[];
    store_locations: IStoreLocationsMap;
    has_restock_permission: boolean;
    default_refund_level: REFUND_LEVEL;
    aggregators_info: IAggregatorInfo[];
    store_currency: string;
}

export enum REFUND_LEVEL {
    LINE_ITEM_REFUND_LEVEL = 'LineItem',
    ORDER_REFUND_LEVEL = 'Order',
}

export interface IRMAReturnMethodPreset {
    id: number;
    internal_name: string;
    type: RETURN_METHOD_TYPE;
}

interface IStoreLocationsMap {
    [location_id: number]: IStoreLocation;
}

export interface IStoreLocation {
    name: string;
    location_id: string;
    fulfillsOnlineOrders: boolean;
    /** Can be extended on demand, since each location has more fields */
}

export interface IRMAEmailLog {
    id: number;
    label: string;
    type: string;
    translation_key: string;
}

export interface IRmaProduct {
    id: number;
    image: string;
    product_name: string;
    variant_name: string | null;
    sku: string | null;
    price: string;
    balance: string;
}
export interface IUploadedMediaFile {
    is_saved: boolean;
    is_image: boolean;
    is_video: boolean;
    absolute_url: string;
}

export interface IRmaProductCase {
    restocked_comment: string | null;
    returned_comment: string | null;
    id: number;
    is_rejected: boolean;
    rejected_reason: string | null;
    product: IRmaProduct;
    merchant_return_reason: IRmaReturnReason;
    product_case_offers: IProductCaseOffer[];
    paid_price: number;
    questions: IProductCaseQuestion[];
    exchange: IExchange;
    answers: string | null;
    _uploaded_media_files: IUploadedMediaFile[];
    is_returned: boolean | null;
    is_restockable: boolean | null;
    is_restocked: boolean | null;
    return_method: IReturnMethod;
    shipment_id?: number;
    _credited_amount: string;
    _automatically_credited_amount: string;
    _automatically_credited: boolean;
    _gifted_amount: string;
    _automatically_gifted_amount: string;
    _automatically_gifted: boolean;
    _is_refunded: boolean;
    _is_successfully_refunded: boolean;
    _refunded_amount: number;
    _bank_refunded_amount: number;
    _external_refund_amount: number;
    _is_automatically_refunded: boolean;
    // In addition to "is_restocked" Model field, we maintain computed property _is_restocked which
    // is computed on the backend when  we send request to the /rma/{id} API endpoint
    _is_restocked: boolean | null;
    // In addition to "is_restockable" Model field, we maintain computed property _is_restockable which
    // is computed on the backend when  we send request to the /rma/{id} API endpoint
    _is_restockable: boolean;
    _restocked_platforms: string | null;
    _bxgy_discount_data: IBxGyDiscountData | null;
    _cap_discount_data: ICapDiscountData | null;
    _items_balance: IItemBalanceBreakdownDto | null;
    validation_images: IValidateItemImage[];
    _is_hidden_resolution: boolean;
    has_incentive_from_switch_to_exchange: boolean;
    _can_reject: ICanReject
}

export interface ICanReject {
    reason: CANT_REJECT_ITEM_REASONS | null;
    result: boolean;
}

export enum CANT_REJECT_ITEM_REASONS {
    DRAFT_FEE_ORDER_IS_CREATED_AND_IN_STATUS_PAID = 'DRAFT_FEE_ORDER_IS_CREATED_AND_IN_STATUS_PAID',
    INCREASED_AMOUNT_TO_PAY_VIA_PORTAL_CHECKOUT = 'INCREASED_AMOUNT_TO_PAY_VIA_PORTAL_CHECKOUT',
    LAST_ITEM_WITH_EXCHANGE_RESOLUTION_TYPE = 'LAST_ITEM_WITH_EXCHANGE_RESOLUTION_TYPE',
    ITEM_IS_ALREADY_REJECTED = 'ITEM_IS_ALREADY_REJECTED',
    RMA_IS_NOT_IN_PENDING_APPROVAL = 'RMA_IS_NOT_IN_PENDING_APPROVAL',
    FEATURE_IS_NOT_SUPPORTED = 'FEATURE_IS_NOT_SUPPORTED',
    CAN_NOT_REJECT_LAST_ITEM = 'CAN_NOT_REJECT_LAST_ITEM',
    LAST_ITEM_WITH_SHIPPING_REQUIRED_METHOD = 'LAST_ITEM_WITH_SHIPPING_REQUIRED_METHOD',
}

export interface IBxGyDiscountData {
    discount_code: string;
    is_buy: boolean;
    is_get: boolean;
}

export interface ICapDiscountData {
    discount_amount: number;
    discount_percentage: number;
}
export interface IMoneyDto {
    amount: number,
    currency_code: string,
}

interface IDeductionDto {
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    order_shipping_cost: IMoneyDto[],
    price_difference: IMoneyDto[],
}

export interface IOtherItemsPaymentsDto {
    product_case_id: number,
    total_amount: IMoneyDto[],
}

interface IPaymentsDto {
    is_paid: boolean,
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    price_difference: IMoneyDto[],
}

export enum ORIGINAL_ITEM_PRICE_SOURCE {
    PAID_PRICE = 'paid_price',
    CATALOG_PRICE = 'catalog_price',
    CATALOG_PRICE_TIME_OF_PURCHASE = 'catalog_price_time_of_purchase',
}

export const ORIGINAL_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP = {
    [ORIGINAL_ITEM_PRICE_SOURCE.PAID_PRICE]: 'PAID_PRICE' ,
    [ORIGINAL_ITEM_PRICE_SOURCE.CATALOG_PRICE]: 'CATALOG_PRICE',
    [ORIGINAL_ITEM_PRICE_SOURCE.CATALOG_PRICE_TIME_OF_PURCHASE]: 'CATALOG_PRICE_TIME_OF_PURCHASE',
}

export enum NEW_ITEM_PRICE_SOURCE {
    OVERRIDE_THE_ORIGINAL_REQUEST = 'OVERRIDE_THE_ORIGINAL_REQUEST',
    FOLLOW_THE_ORIGINAL_REQUEST = 'FOLLOW_THE_ORIGINAL_REQUEST',
}

export const NEW_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP = {
    [NEW_ITEM_PRICE_SOURCE.OVERRIDE_THE_ORIGINAL_REQUEST]: 'OVERRIDE_THE_ORIGINAL_REQUEST',
    [NEW_ITEM_PRICE_SOURCE.FOLLOW_THE_ORIGINAL_REQUEST]: 'FOLLOW_THE_ORIGINAL_REQUEST',
}

export interface IItemBalanceBreakdownDto {
    balance: IMoneyDto[],
    original_item_paid_price: IMoneyDto[],
    original_item_price_for_exchange: IMoneyDto[],
    original_item_price_source: ORIGINAL_ITEM_PRICE_SOURCE | null,
    price_difference_adjustments: IMoneyDto[],
    new_item_price: IMoneyDto[],
    new_item_price_source: NEW_ITEM_PRICE_SOURCE | null,
    new_item_price_difference_adjustments: IMoneyDto[],
    new_item_bxgy_discount_adjustment: IMoneyDto[],
    new_item_applied_discount: IMoneyDto[],
    new_item_discounts: string[],
    price_difference: IMoneyDto[],
    exchange_range_adjustment: IMoneyDto[],
    switch_to_exchange_incentive: IMoneyDto[],
    refund_adjustments: IMoneyDto[],
    compare_at_price_discount: IMoneyDto[],
    compare_at_price_discount_percentage: number | null,
    deduction: IDeductionDto,
    pending_payments: IPaymentsDto,
    paid_by_other_items: IOtherItemsPaymentsDto[],
    paid_for_other_items: IOtherItemsPaymentsDto[],
    discount_fee: IMoneyDto[],
    restocking_fee: IMoneyDto[],
    return_label_fee: IMoneyDto[],
    rma_fee: IMoneyDto[],
    cancellation_fee: IMoneyDto[],
    original_item_catalog_price_at_time_of_purchase: IMoneyDto[],
    original_item_compare_at_price: IMoneyDto[],
    applied_bxgy_discount: IMoneyDto[],
    applied_basic_discount: IMoneyDto[],
}

export interface IValidateItemImage{
    _full_url: string;
    id: number;
    filename: string;
    is_saved: boolean;
    _is_deleted?: boolean;
}

export interface IProductCaseOffer {
    credits: number,
    switch_to_exchange_incentive: number|null,
    incentive_virtual_currency: INCENTIVE_VIRTUAL_CURRENCIES | null,
    return_label_fee: number|null,
    fee: number|null,
    status: PRODUCT_CASE_OFFER_STATUS,
    policy_rule: IRmaPolicyRule;
}
export interface IExchange {
    id: number;
    image: string;
    new_item_product_name: string;
    new_item_variant_title: string;
    new_item_image_url: string | null;
    sku: string | null;
    quantity: number;
    new_item_catalog_price_time_of_request: number;
    new_item_catalog_price_time_of_draft_order: number;
    new_item_catalog_price_time_of_draft_order_release: number;
    new_item_catalog_price_currency_code: string | null;
    new_item_contextual_price_time_of_request: number;
    new_item_contextual_price_time_of_draft_order: number | null;
    new_item_contextual_price_time_of_draft_order_release: number | null;
    new_item_contextual_price_currency_code: string | null;
    new_item_adjusted_price: number | null;
    new_item_adjusted_price_currency: string | null;
    variant_id: string;
    product_case_id: number | null;
    discounts: string[] | null;
    _applied_discount: IMoneyDto[] | null;
    _bxgy_discount_adjustment: IMoneyDto[] | null;
}
export interface IProductCaseQuestion{
    id: number;
    translation: string;
    answers: IProductCaseAnswer[];
    images: IRMAQuestionMedia[];
}

export interface IRMAQuestionMedia {
    _full_url: string;
    id: number;
    filename: string;
    is_saved: boolean;
}

export interface IProductCaseAnswer {
    id: number;
    answer: string;
    translation: string;
}

export interface IRmaShipment {
	id: number;
	status: SHIPMENT_STATUS_ENUM;
    tracking_number: string | null;
    tracking_url: string | null;
    return_label_url: string | null;
    qr_image_url: string | null;
    qr_request_json: string | null;
    packing_slip_url: string | null;
    carrier: string | null;
    is_outbound_shipment: boolean;
    _service_level: string | null;
    _aggregator_code: SHIPPING_CONNECTION_CODE | null;
}

export interface IRmaCustomerCase {
	id: number;
    locale: string;
    accepted_product_cases: IRmaProductCase[];
    payment_currency_code: string;
    order_name:string;
    glbe_order_id: string|null;
    customer_id: string;
    conversion_rate: number;
    return_label_fee: number|null;
    rma_fee: number|null;
    rma_score: string|null;
    rma_score_reason: string|null;
    cancellation_fee: number|null;
    payments: IRmaPyment[];
    exchange_orders: IRmaExchangeOrder[];
    is_gift: boolean;
    address_id: string|null,
    location_id: string|null,
    _reserve_item_count: number;
    _reserve_until: string|null;
    _number_of_items_message: string;
    _should_create_fee_draft_order: boolean;
    _should_return_label_fee_be_always_charged: boolean;
    _total_product_cases_count: number;
    _updated_shipping_address?: string;
    exchanges: IExchange[];
    used_coverage: boolean;
}
export interface IRmaPyment {
    payment_breakdown: string;
    status: PAYMENT_STATUS;
    payment_id: string;
    _full_amount?: number;
    _discounted_amount?: number;
    _currency?: string;
    _authorization_expiration_days_left?: number;
    _authorized_date?: string;
    _authorization_expiration_date?: string;
    _capture_date?: string;
    _cancel_date?: string;
    _captured_amount?: number;
    _is_hidden: boolean;
}
export interface IRmaExchangeOrder{
    _order_link: string | null;
    _draft_order_link: string;
    release_date: string|null;
    reserve_until: string;
    draft_fee_order_id: string | null;
    order_name: string;
    order_id: string | null;
    draft_order_id: string;
    draft_order_type: DRAFT_ORDER_TYPE;
    internal_state: DRAFT_ORDER_STATE;
    total_amount_at_the_time_of_draft_order_creation: number;
}
export interface IRmaError {
    id: number;
    short_error: string | null;
    is_resolved: boolean;
    classified_errors?: IClassifiedError[] | null;
}

export interface IRma {
    id: number;
    created_at: string;
    customer_case: IRmaCustomerCase;
    return_errors: IRmaError[];
    shipments: IRmaShipment[];
    status: RMA_STATUS;
    is_restock_validated: boolean;
    _status: RMA_STATUS_TRANSLATION_KEY;
    _overall_shipment_status: SHIPMENT_STATUS_ENUM|null;
    _rma_return_method: IReturnMethod;
    _email_logs: IRMAEmailLog[];
    timeline: ITimelineItemDto[];
    required_manager_approval: boolean;
    required_manager_approval_reason?: string;
    is_out_of_policy: boolean;
}

export interface IRmaActionStore {
    get newReturnMethodId(): number|undefined;
    get reasonField(): IActionReasonField | undefined;
    get emailAttachments(): IEmailAttachment[];
    get emailAdditionalContent(): string | undefined;
    get sendEmailNotification(): boolean;
    get returnMethodWarning(): string | null;
    get showReturnMethodChangeWarning(): boolean;
    get restockingLocationId(): string | undefined;
    get isProcessing(): boolean;

    addEmailAttachment(attachment: IEmailAttachment): void;
    removeEmailAttachment(attachment: IEmailAttachment): void;
    setSendEmailNotification(v: boolean): void;
    setEmailAdditionalContent(v: string): void;
    setRestockingLocationId(v: string): void;
    setSelectedProductCaseIds(v: number[]): void;
    resetReturnMethodChange(): void;
    confirmReturnMethodChange(): void;
    confirmReturnMethodsChangeWithLabelFees(): void;
    confirmReturnMethodsChangeWithoutLabelFees(): void;
    setNewReturnMethodId(v: number): void;

    hasCustomerEmail(): boolean;
    submitAction(closeCb?: () => void): Promise<void>;
}

export interface IActionReasonField {
    label: string,
    value: string | undefined,
    onChange: (v: string) => void
}

export interface IRmaApproveRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    new_return_method_id?: number;
    cancel_return_label_fee: boolean;
    manager_approval_credentials?: IManagerCredentials;
}

export interface IReceiveShipmentRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
}

export interface IRmaRejectRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    reject_reason: string;
}

export interface IRmaRejectItemRequest {
    product_case_id: number
    reject_reason: string|null
}

export interface IRmaReopenReuest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
}

export interface IRmaCancelRequest {
    send_email_notification: boolean;
    email_attachment: IEmailAttachment[];
    email_additional_content?: string;
    cancel_reason: string;
}

export interface IRestockRequest {
    selected_product_case_ids: number[];
    restocking_location_id?: string;
}

export interface IRmaAddNoteRequest {
    note: string;
}

export interface IValidationImageUpload{
    filename: string;
    product_case_validation_id: number;
    type: string;
    base64FileData: string;
}

export interface IValidateItemsActionRequest {
    images_to_upload: IValidationImageUpload[],
    images_to_delete: IValidateItemImage[],
    is_returned: boolean,
    returned_comment: string,
    is_restockable: boolean,
    restocked_comment: string,
    product_case_id: number
}

export interface IEmailAttachment {
    name: string;
    type: string;
    base64: string;
}

export interface IRmaData {
	rma: IRma;
	settings: IRmaSettings;
}

export interface ITimelineItemDto {
    type: TIMELINE_ITEM_TYPE_ENUM;
    content: string;
    return_error: IRmaError | null;
    created_at: string;
    is_system: boolean;
    is_automation: boolean;
    user_name: string;
    transaction_data?: ITransactionDataDto;
}

export interface IClassifiedError {
    category: string;
    code: string;
    created_at: string;
    id: number;
    is_category_exclusive: boolean;
    message: string;
    recommended_action: string;
    subcategory: string;
    type: string;
    updated_at: string;
    weight: number;
}

export interface ITransactionDataDto {
    platform: string;
    status: RMA_EXTERNAL_TRANSACTION_STATUS;
    transaction_id: number;
}

export enum TIMELINE_ITEM_TYPE_ENUM {
    TYPE_CREDITED = 'credited',
    TYPE_REFUNDED = 'refunded',
    TYPE_ERROR = 'error',
    TYPE_NOTE = 'note',
    TYPE_EMAIL = 'email',
}

export enum SHIPMENT_STATUS_ENUM {
	PENDING_SHIPMENT = 0,
	LABEL_CREATED = 10,
	SHIPPED = 2,
	RECEIVED = 3,
    REJECTED = 4,
    VERIFIED = 5,
    OUT_FOR_DELIVERY = 6,
    ERROR = 7
};

export const SHIPMENT_STATUS_TRANSLATION_KEYS_MAP = {
	[SHIPMENT_STATUS_ENUM.PENDING_SHIPMENT]: 'WAITING_STATUS',
	[SHIPMENT_STATUS_ENUM.LABEL_CREATED]: 'LABEL_CREATED',
	[SHIPMENT_STATUS_ENUM.SHIPPED]: 'SHIPMENT_SHIPPED_STATUS',
    [SHIPMENT_STATUS_ENUM.RECEIVED]: 'SHIPMENT_DELIVERED_STATUS',
    [SHIPMENT_STATUS_ENUM.REJECTED]: 'SHIPMENT_REJECTED_STATUS',
    [SHIPMENT_STATUS_ENUM.OUT_FOR_DELIVERY]: 'SHIPMENT_OUT_FOR_DELIVERY_STATUS',
	[SHIPMENT_STATUS_ENUM.ERROR]: 'SHIPMENT_STATUS_ERROR',
    [SHIPMENT_STATUS_ENUM.VERIFIED]: 'SHIPMENT_VERIVIED_STATUS'
} as const;

export enum RMA_STATUS {
    PENDING_APPROVAL = 0,
    APPROVED = 1,
    REJECTED = 2,
    DONE = 3,
    RECEIVED = 4,
    VALIDATED = 5,
    CANCELED = 6,
}

export enum DRAFT_ORDER_TYPE {
    DRAFT_ORDER_TYPE_FEE = 'DRAFT_ORDER_TYPE_FEE',
    DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE = 'DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE',
    DRAFT_ORDER_TYPE_EXCHANGE = 'DRAFT_ORDER_TYPE_EXCHANGE',
    DRAFT_ORDER_TYPE_INSTANT_EXCHANGE = 'DRAFT_ORDER_TYPE_INSTANT_EXCHANGE',
    DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT = 'DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT',
    DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT = 'DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT',
}

export const DRAFT_ORDER_EXCHANGE_TYPES = [
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_EXCHANGE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_INSTANT_EXCHANGE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_EXCHANGE_VIA_PORTAL_CHECKOUT
];
export const DRAFT_ORDER_FEE_TYPES = [
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_FEE,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_FEE_VIA_PORTAL_CHECKOUT,
    DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_INSTANT_EXCHANGE_CAPTURE,
];

export enum DRAFT_ORDER_STATE {
    DRAFT_ORDER_CREATED = 'DRAFT_ORDER_CREATED',
    PENDING_INVOICE = 'PENDING_INVOICE',
    PENDING_PAYMENT = 'PENDING_PAYMENT',
    INVOICE_PAID = 'INVOICE_PAID',
}

export enum PAYMENT_STATUS {
    AUTHORIZED = 'requires_capture',
    CANCELED = 'canceled',
    CHARGED = 'succeeded',
};

export enum PRODUCT_CASE_OFFER_STATUS {
    DECLINED = 0,
    OFFERED = 1,
    ACCEPTED = 2,
}

export enum MANAGERS_APPROVAL_REASONS {
    OUT_OF_POLICY = 'out of policy',
    ORDER_OUT_OF_POLICY = 'Order out of policy',
};

export interface IGetTranslationData {
    scope: string,
    key: string
};

export interface IManagersApprovalTranslations {
    noteKey?: IGetTranslationData,
    tooltipKey?: IGetTranslationData,
    indicatorKey?: IGetTranslationData
}

export function getManagersApprovalReasonTranslationKeys(reason: MANAGERS_APPROVAL_REASONS): IManagersApprovalTranslations | undefined {
    switch (reason) {
        case MANAGERS_APPROVAL_REASONS.OUT_OF_POLICY: {
            return {
                noteKey: {
                    scope: 'dashboard-create-rma',
                    key: 'THIS_RMA_IS_OUT_OF_POLICY_NOTE_TEXT'
                },
                tooltipKey: {
                    scope: 'dashboard-create-rma',
                    key: 'OOP_NOT_HAVE_APPROVE_PERMISSION_TOOLTIP_TEXT'
                },
                indicatorKey: {
                    scope: 'dashboard-returns-page',
                    key: 'RMA_OUT_OF_POLICY_INDICATOR_TEXT'
                }
            }
        }
        /* This distinction is to fetch relevant keys for either order/rma oop */
        case MANAGERS_APPROVAL_REASONS.ORDER_OUT_OF_POLICY: {
            return {
                indicatorKey: {
                    scope: 'dashboard-create-rma',
                    key: 'ORDER_OUT_OF_POLICY_INDICATOR_TEXT'
                },
            }
        }
    }

    return undefined;
};

export enum RMA_EXTERNAL_TRANSACTION_STATUS {
    PLATFORM_ORDER_TRANSACTION_STATUS_AWAITING_RESPONSE = 'AWAITING_RESPONSE',
    PLATFORM_ORDER_TRANSACTION_STATUS_ERROR = 'ERROR',
    PLATFORM_ORDER_TRANSACTION_STATUS_PENDING = 'PENDING',
    PLATFORM_ORDER_TRANSACTION_STATUS_FAILURE = 'FAILURE',
    PLATFORM_ORDER_TRANSACTION_STATUS_SUCCESS = 'SUCCESS',
    PLATFORM_ORDER_TRANSACTION_STATUS_UNKNOWN = 'UNKNOWN',
}
